import { Global, css } from "@emotion/react";
import "@hightouch/htui/fonts.css";

export const GlobalStyles = () => (
  <>
    <Global styles={base} />
    <Global styles={reset} />
    <Global styles={ace} />
    <Global styles={algolia} />
    <Global styles={googlePicker} />
    <Global styles={scrollbarStyles} />
    <Global styles={textSelectionStyles} />
    <Global styles={pylonChat} />
  </>
);

const base = () => css`
  html {
    font-weight: 400;
    font-size: 13px;
    background-color: var(--chakra-colors-base-background);
    scrollbar-gutter: stable;
  }

  body {
    overscroll-behavior: none;
  }
  /* The stable gutter keeps the scrollbar's space when a scroll lock hides it, so react-remove-scroll's matching body margin would narrow the page. */
  html body[data-scroll-locked] {
    margin-right: 0 !important;
  }

  #react {
    height: 100%;
    display: flex;
  }
  /**
   * Remove outline from
   * - all anchor tags
   * - span elements (tooltips)
   */
  a:active,
  a:focus,
  span {
    outline: none;
  }
  strong {
    font-weight: 500;
  }
`;

const reset = () => css`
  /**
 * Manually forked from SUIT CSS Base: https://github.com/suitcss/base
 * A thin layer on top of normalize.css that provides a starting point more
 * suitable for web applications.
 */
  /**
 * Removes the default spacing and border for appropriate elements.
 */
  button {
    background-color: transparent;
    background-image: none;
  }
  fieldset {
    margin: 0;
    padding: 0;
  }
  ol,
  ul {
    margin: 0;
    padding-left: 14px;
  }
  textarea {
    resize: vertical;
  }
  input::placeholder,
  textarea::placeholder {
    opacity: 1;
  }
  button,
  [role="button"] {
    cursor: pointer;
  }
  table {
    border-collapse: collapse;
  }
  button,
  input,
  optgroup,
  select,
  textarea {
    padding: 0;
    line-height: inherit;
    color: inherit;
  }
  /**
  * Make replaced elements display: block by default as that's
  * the behavior you want almost all of the time. Inspired by
  * CSS Remedy, with svg added as well.
  *
  * https://github.com/mozdevs/cssremedy/issues/14
  */
  img,
  svg,
  video,
  canvas,
  audio,
  iframe,
  embed,
  object {
    display: block;
    vertical-align: middle;
  }
  /**
  * Constrain images and videos to the parent width and preserve
  * their instrinsic aspect ratio.
  *
  * https://github.com/mozdevs/cssremedy/issues/14
  */
  img,
  video {
    max-width: 100%;
    height: auto;
  }
`;

const ace = css`
  .ace_editor {
    border-radius: 3px;
    border: 1px solid var(--chakra-colors-base-border) !important;
  }
  .ace_gutter {
    background-color: var(--chakra-colors-gray-100) !important;
  }
  .ace_gutter-active-line {
    background-color: var(--chakra-colors-teal-500) !important;
    color: white !important;
  }
  .ͼf {
    color: var(--chakra-colors-orange-text);
  }
`;

const algolia = css`
  .ais-SearchBox-form {
    display: flex;
    width: 100%;
    align-items: center;
    position: relative;
  }
  .ais-SearchBox-input {
    width: 100%;
    padding: 20px;
    padding-left: 52px;
    font-size: 15px;
    border: 2px solid rgb(233, 236, 245);
    border-radius: 4px;
    min-height: 32px;
    background-color: white;
    flex: 1;
    transition: border-color 150ms;
    outline: none !important;
    display: flex;
    align-items: center;
    &:hover,
    &:focus {
      border-color: var(--chakra-colors-gray-600);
    }
    &::-webkit-search-cancel-button {
      display: none;
    }
  }
  .ais-SearchBox-submit {
    position: absolute;
    left: 16px;
  }
  .ais-SearchBox-reset {
    position: absolute;
    right: 16px;
    border: none;
    &:focus:not(:focus-visible) {
      outline: none !important;
    }
  }
  .ais-SearchBox-input::placeholder {
    color: var(--chakra-colors-gray-600);
    font-size: 15px;
  }
  .ais-SearchBox-resetIcon {
    width: 16px;
    height: 16px;
    fill: var(--chakra-colors-gray-600);
  }
  .ais-SearchBox-loadingIndicator,
  .ais-SearchBox-submit {
    display: none;
  }
`;

const googlePicker = css`
  .picker-dialog-bg {
    z-index: 20000 !important;
  }
  .picker-dialog {
    z-index: 20001 !important;
  }
`;

const scrollbarStyles = css`
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--chakra-colors-base-border);
    border-radius: 4px;
    border: none;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border: none;
  }

  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  * {
    scrollbar-width: thin;
    scrollbar-color: var(--chakra-colors-base-border) transparent;
  }
`;

const textSelectionStyles = css`
  ::selection {
    background-color: var(--chakra-colors-primary-background);
    color: var(--chakra-colors-primary-hover);
  }

  ::-moz-selection {
    background-color: var(--chakra-colors-primary-background);
    color: var(--chakra-colors-primary-hover);
  }
`;

// Dock the Pylon chat window against the left nav (the launcher is hidden; we
// open it from a custom nav item). --ht-nav-width is published on <html> by
// NavWidthProvider, so this tracks expand/collapse with no JS. Pylon exposes no
// positioning API — overriding these container classes is their documented
// approach, but the classes are internal and may change on widget updates.
const pylonChat = css`
  .PylonChat-bubbleFrameContainer {
    opacity: 0 !important;
    pointer-events: none !important;
  }
  .PylonChat-chatWindowFrameContainer {
    left: calc(var(--ht-nav-width, 56px) + 8px) !important;
    right: auto !important;
    bottom: 16px !important;
    top: auto !important;
    transform-origin: left !important;
    border: none !important;
    border-radius: 16px !important;
    overflow: hidden !important;
    box-shadow:
      0px 0px 1px 0px rgba(0, 0, 0, 0.2),
      0px 1px 6px 0px rgba(0, 0, 0, 0.04),
      0px 12px 20px -2px rgba(0, 0, 0, 0.08) !important;
    /* Replace Pylon's default (large) open animation with a subtle scale that
       matches our own popovers: scale 0.95 + easeOutCubic (our menu easing). */
    opacity: 1 !important;
    transform: scale(1) !important;
    pointer-events: auto !important;
    transition:
      transform 150ms cubic-bezier(0.215, 0.61, 0.355, 1),
      opacity 150ms cubic-bezier(0.215, 0.61, 0.355, 1) !important;
  }
  /* When closed the container stays in the DOM at nav-hugging position; without
     pointer-events:none its invisible frame overlaps and swallows hover/clicks
     on the nav popovers (Resources, user menu) that open in the same region. */
  .PylonChat-chatWindowFrameContainer--closed {
    opacity: 0 !important;
    transform: scale(0.95) !important;
    pointer-events: none !important;
  }
`;
